import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	input,
	InputSignal,
	OnDestroy,
	Signal,
	signal,
	viewChild,
	WritableSignal,
} from '@angular/core'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@Component({
	selector: 'app-carousel',
	templateUrl: './carousel.component.html',
	styleUrl: './carousel.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [ButtonComponent],
	host: {
		'[style.--trigger-width]': 'triggerWidth()',
	},
})
export class CarouselComponent implements AfterViewInit, OnDestroy {
	protected readonly math: Math = Math

	public arrowSize: InputSignal<string> = input<string>('16px')
	public triggerWidth: InputSignal<string> = input<string>('40px')

	private _wrapper: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('wrapper')
	private _track: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('track')
	private _resizeObserver: ResizeObserver | null = null

	protected offsetX: WritableSignal<number> = signal<number>(0)
	protected isOverflow: WritableSignal<boolean> = signal<boolean>(false)
	protected isShowPrevBtn: WritableSignal<boolean> = signal<boolean>(true)
	protected isShowNextBtn: WritableSignal<boolean> = signal<boolean>(true)

	protected trackTransform: Signal<string> = computed<string>((): string => `translateX(-${this.offsetX()}px)`)

	private _maxOffset(): number {
		const trackEl: HTMLElement = this._track().nativeElement
		const wrapperEl: HTMLElement = this._wrapper().nativeElement
		return this.math.max(0, trackEl.offsetWidth - wrapperEl.offsetWidth)
	}

	private _calculateScrollStep(): number {
		const trackEl: HTMLElement = this._track().nativeElement
		const firstChild: HTMLElement | null = trackEl.firstElementChild as HTMLElement | null
		if (!firstChild) return 0
		const gap: number = parseFloat(getComputedStyle(trackEl).gap) || 0
		return firstChild.offsetWidth + gap
	}

	private _checkVisibleTriggers(): void {
		const maxOffset: number = this._maxOffset()
		this.isOverflow.set(maxOffset > 0)

		if (this.offsetX() > maxOffset) {
			this.offsetX.set(maxOffset)
		}

		this._changeShowTriggers()
	}

	private _changeShowTriggers(): void {
		this.isShowPrevBtn.set(this.offsetX() <= 0)
		this.isShowNextBtn.set(this.offsetX() >= this._maxOffset())
	}

	protected goNext(): void {
		const step: number = this._calculateScrollStep()
		const max: number = this._maxOffset()
		this.offsetX.update((o: number): number => this.math.min(o + step, max))
		this._changeShowTriggers()
	}

	protected goPrev(): void {
		const step: number = this._calculateScrollStep()
		this.offsetX.update((o: number): number => this.math.max(o - step, 0))
		this._changeShowTriggers()
	}

	ngAfterViewInit(): void {
		this._checkVisibleTriggers()
		this._resizeObserver = new ResizeObserver((): void => this._checkVisibleTriggers())
		this._resizeObserver.observe(this._wrapper().nativeElement)
		this._resizeObserver.observe(this._track().nativeElement)
	}

	ngOnDestroy(): void {
		if (this._resizeObserver) {
			this._resizeObserver.disconnect()
			this._resizeObserver = null
		}
	}
}
